// 範圍（SPEC #vms-web-220 頂列範圍切換器、#me-devices、#org-model-products）：
//   - 資料 = 各 plane 的 GET /v1/me/devices（三種來源合併、去重、每台的 actions）＋ GET /v1/me/orgs（角色、組織層權限、MFA 要求）
//   - 預設範圍「全部」（我自己的 + 各組織授權給我的 + 別人分享給我的）；可選某一種來源 → 組織 → site 子樹 → 家戶，或某個分享者
//   - 設備樹、即時影像牆、事件、回放都吃這一份（visible / visibleDevices）；每個瀏覽器記住上次選擇
//   - 組織管理類頁面（使用者與權限、稽核、帳單、設備庫存、設定）一次只對一個組織：範圍指到的組織（org）
//   - 組織樹 / 檢視 / 群組（SPEC #org-tree-ui 第 1 點）：左側面板點節點 = 範圍切到那個子樹。這些範圍由伺服器展開（lib/remoteScope.ts：
//     「我的設備」的 node_id / delegation_id 篩選、檢視 / 群組的 resolve），這裡只拿回設備 key 再從「我的設備」挑，每台的 actions 照舊
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { homeLabels } from './devices';
import { useTranslation } from 'react-i18next';
import { scopeNodeName } from './scopeName';
import { PATH_SEP } from './orgRoot';
import { useQueries, useQuery } from '@tanstack/react-query';
import { getHomeOf, type HomeDetail } from '@kinio/web-sdk';
import { useApp } from './AppContext';
import { meOrgs, planesOf, type MyOrg, type Plane, type ScopeHome } from './planeApi';
import { fetchRemoteScope, isRemoteScope, orgOfRemote } from './remoteScope';
import {
  EMPTY_MY_DEVICES,
  nodePath,
  devicesInScope,
  fetchMyDevices,
  homeRoleOf,
  homesInScope,
  mergeMyDevices,
  orgOfScope,
  scopeHomes,
  scopeTree,
  validScope,
  canonicalScope,
  type MyDevice,
  type MyDevicesData,
  type ScopeHomeInfo,
  type ScopeKey,
  type ScopeNode,
} from './myDevices';

export type { ScopeKey } from './myDevices';

const SCOPE_KEY = 'kinio.vms.scope';
const SCOPE_LABEL_KEY = 'kinio.vms.scopeLabel';

export interface ScopeState {
  planes: Plane[];
  /** 所有可以去的家戶（我組織裡的 + 只經分享看到設備的）。 */
  homes: ScopeHome[];
  /** 目前範圍內的家戶（頁面用這個）。 */
  visible: ScopeHome[];
  /** 「我的設備」全部（三種來源、去重）。 */
  devices: MyDevice[];
  /** 目前範圍內的設備。 */
  visibleDevices: MyDevice[];
  /** 範圍切換器 / 設備樹的分組節點。 */
  tree: ScopeNode[];
  /** GET /v1/me/orgs（個人組織第一筆）。 */
  orgs: MyOrg[];
  data: MyDevicesData;
  scope: ScopeKey;
  /**
   * label：組織樹 / 檢視 / 群組的範圍（伺服器展開的）在範圍切換器上的名稱（路徑）。
   * announce：不是使用者在範圍切換器上點的變更（組織膠囊、深連結提示…）要 toast「已把範圍切到 <名稱>」（SPEC #vms-web-220、UX-15）
   */
  setScope(k: ScopeKey, label?: string, opts?: { announce?: string }): void;
  /** 自動切換範圍的提示（殼層顯示幾秒）；null = 沒有 */
  scopeNotice: { text: string; seq: number } | null;
  clearScopeNotice(): void;
  /** 組織樹 / 檢視 / 群組範圍的名稱（範圍切換器、麵包屑） */
  scopeLabel: string;
  /** 使用者切範圍的次數（setScope 一次 +1；頁面用來分辨「使用者切了」與「載入時還原」） */
  scopeSeq: number;
  /** 第一次載入「我的設備」/「我的組織」中（殼層畫骨架，B-29）；之後背景重抓不算 */
  booting: boolean;
  /** 伺服器展開的範圍還在載入 */
  scopeLoading: boolean;
  /** 目前範圍指到的組織（選組織 / site / 家戶 / 外部分享時）；沒有指到、只有一個組織時就是那一個。 */
  org: MyOrg | null;
  loading: boolean;
  /** 抓不到「我的設備」的 plane。 */
  failed: Plane[];
}

const Ctx = createContext<ScopeState | null>(null);

function savedScope(): ScopeKey {
  try {
    return localStorage.getItem(SCOPE_KEY) || 'all';
  } catch {
    return 'all';
  }
}

function savedLabel(): string {
  try {
    return localStorage.getItem(SCOPE_LABEL_KEY) || '';
  } catch {
    return '';
  }
}

/** ScopeHomeInfo → 頁面用的 ScopeHome（KinioHome 形狀，SDK 的家戶 API 直接吃）。detailRole = GET /v1/homes/{id} 的 home.role。 */
export function toScopeHome(h: ScopeHomeInfo, detail?: HomeDetail): ScopeHome {
  const detailRole = typeof detail?.home?.role === 'string' ? (detail.home.role as string) : undefined;
  const access = (detail as { access?: { can_manage?: boolean } } | undefined)?.access;
  return {
    home_id: h.home_id,
    env: h.env,
    display_name: h.display_name,
    role: homeRoleOf(h, detailRole),
    endpoints: { api: h.api } as ScopeHome['endpoints'],
    source: h.source,
    org_id: h.org_id,
    org_kind: h.org_kind,
    site_id: h.site_id,
    timezone: h.timezone,
    external: h.external,
    can_manage: h.can_manage || !!access?.can_manage,
  };
}

export function ScopeProvider({ children }: { children: ReactNode }) {
  const { config, session } = useApp();
  const planes = useMemo(() => planesOf(config, session), [config, session]);
  const userId = session?.user?.user_id ?? '';
  const devQ = useQueries({
    queries: planes.map((p) => ({
      queryKey: ['me-devices', userId, p.api],
      queryFn: () => fetchMyDevices(p),
      staleTime: 30_000,
      refetchInterval: 60_000,
      retry: 1,
    })),
  });
  const orgQ = useQueries({
    queries: planes.map((p) => ({
      queryKey: ['me-orgs', userId, p.api],
      queryFn: () => meOrgs(p),
      staleTime: 60_000,
      retry: 1,
    })),
  });
  const [scope, setScopeState] = useState<ScopeKey>(savedScope);
  const [scopeLabel, setScopeLabel] = useState<string>(savedLabel);
  const [scopeSeq, setScopeSeq] = useState(0);
  const [scopeNotice, setScopeNotice] = useState<{ text: string; seq: number } | null>(null);
  const remote = isRemoteScope(scope);
  const remoteQ = useQuery({
    queryKey: ['me-scope', userId, scope],
    queryFn: () => fetchRemoteScope(planes, scope),
    enabled: remote && planes.length > 0,
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: 1,
  });
  const devData = devQ.map((q) => q.data);
  const devErr = devQ.map((q) => q.isError);
  const orgData = orgQ.map((q) => q.data);
  const data = useMemo(
    () => (planes.length ? mergeMyDevices(planes, devData, devErr) : EMPTY_MY_DEVICES),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(devData), JSON.stringify(devErr), planes],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const orgs = useMemo(() => orgData.flatMap((d) => d ?? []), [JSON.stringify(orgData)]);
  const infos = useMemo(() => scopeHomes(data), [data]);
  // 家戶角色：平台 H 起「我的設備」直接回 role / can_manage；舊後端沒回的家戶才退回 GET /v1/homes/{id}（跟設備樹同一份快取）
  const needDetail = useMemo(() => infos.filter((h) => !h.external && h.home_role === undefined), [infos]);
  const detailQ = useQueries({
    queries: needDetail.map((h) => ({ queryKey: ['home', h.home_id], queryFn: () => getHomeOf({ home_id: h.home_id, env: h.env, role: '', endpoints: { api: h.api } as ScopeHome['endpoints'] }), staleTime: 15_000 })),
  });
  const details = new Map<string, HomeDetail>();
  needDetail.forEach((h, i) => {
    const d = detailQ[i]?.data;
    if (d) details.set(h.home_id, d);
  });
  const detailKey = detailQ.map((q) => q.dataUpdatedAt).join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const homes = useMemo(() => infos.map((h) => toScopeHome(h, details.get(h.home_id))), [infos, detailKey]);
  // 伺服器展開的範圍失敗（節點已看不到、委派結束…）→ 回「全部」
  const effScope = remote ? (remoteQ.isError ? 'all' : scope) : validScope(data, scope) ? canonicalScope(data, scope) : 'all';
  const remoteKeys = remoteQ.data;
  const visibleDevices = useMemo(() => {
    if (!isRemoteScope(effScope)) return devicesInScope(data, effScope);
    const keys = new Set(remoteKeys ?? []);
    return data.devices.filter((d) => keys.has(`${d.home_id}/${d.device_ref}`));
  }, [data, effScope, remoteKeys]);
  const visible = useMemo(() => {
    if (isRemoteScope(effScope)) {
      const ids = new Set(visibleDevices.map((d) => d.home_id));
      const home = effScope.startsWith('home:') ? effScope.slice(5) : '';
      return homes.filter((h) => ids.has(h.home_id) || h.home_id === home);
    }
    const ids = new Set(homesInScope(data, infos, effScope).map((h) => h.home_id));
    return homes.filter((h) => ids.has(h.home_id));
  }, [data, infos, homes, effScope, visibleDevices]);
  const tree = useMemo(() => scopeTree(data), [data]);
  const org = useMemo(() => {
    const id = isRemoteScope(effScope) ? orgOfRemote(effScope) : orgOfScope(data, effScope);
    if (id) return orgs.find((o) => o.org_id === id) ?? null;
    return orgs.length === 1 ? orgs[0] : null;
  }, [data, effScope, orgs]);

  function setScope(k: ScopeKey, label = '', opts?: { announce?: string }) {
    if (opts?.announce && k !== scope) setScopeNotice((n) => ({ text: opts.announce!, seq: (n?.seq ?? 0) + 1 }));
    setScopeState(k);
    setScopeLabel(label);
    if (k !== scope) setScopeSeq((n) => n + 1);
    try {
      localStorage.setItem(SCOPE_KEY, k);
      localStorage.setItem(SCOPE_LABEL_KEY, label);
    } catch {
      /* 無 storage */
    }
  }

  const value: ScopeState = {
    planes,
    homes,
    visible,
    devices: data.devices,
    visibleDevices,
    tree,
    orgs,
    data,
    scope: effScope,
    setScope,
    scopeNotice,
    clearScopeNotice: () => setScopeNotice(null),
    scopeSeq,
    scopeLabel: isRemoteScope(effScope) ? scopeLabel : '',
    scopeLoading: remote && remoteQ.isLoading,
    booting: devQ.some((q) => q.isLoading) || orgQ.some((q) => q.isLoading),
    org,
    loading: devQ.some((q) => q.isLoading) || orgQ.some((q) => q.isLoading) || (remote && remoteQ.isLoading),
    failed: data.failed,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useScope(): ScopeState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useScope 要在 ScopeProvider 裡');
  return v;
}

/** 家戶在下拉選單的名稱：同名的帶組織名（B-23：「lab、lab、rd、rd」分不出來）。 */
/**
 * 家戶的顯示路徑：全站同一種寫法，從組織名起算、中間的場域都寫（「LivingStyle › lstw › lab」；第四輪 B4-10、B4-11，SPEC #vms-web-220 路徑函式）。
 * 範圍樹還沒有這一戶（剛載入、分享來的）時退回「組織 › 家戶」。
 */
export function useHomeLabel(): (h: { home_id: string; display_name?: string }) => string {
  const { t } = useTranslation();
  const { homes, orgs, tree } = useScope();
  const m = useMemo(() => homeLabels(homes, (id) => orgs.find((o) => o.org_id === id)?.display_name), [homes, orgs]);
  return (h) => {
    const p = nodePath(tree, 'home:' + h.home_id)
      .filter((n) => n.kind !== 'source')
      .map((n) => scopeNodeName(t, n))
      .filter(Boolean);
    if (p.length > 1) return p.join(PATH_SEP);
    return m.get(h.home_id) ?? (h.display_name || h.home_id);
  };
}
